import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Copy,
  CheckCircle2,
  Clock,
  XCircle,
  ArrowUpRight,
  MessageCircle,
  Landmark,
  Check,
  AlertCircle,
  X,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  useTransfer,
  useRecipient,
  useFundingInstructions,
} from "../hooks/useMe";
import { PrivyCardOnrampButton } from "./transfer/PrivyCardOnrampButton";

const STATUS_DISPLAY = {
  completed: {
    label: "Delivered",
    icon: CheckCircle2,
    pill: "bg-[#F5F2EB] text-[#0A4730] border border-[#EBE5D8]",
  },
  confirmed: {
    label: "In Progress",
    icon: Clock,
    pill: "bg-[#F5F2EB]/90 text-[#6B5A23] border border-[#EBE5D8]",
  },
  draft: {
    label: "Pending",
    icon: Clock,
    pill: "bg-[#F5F2EB]/90 text-[#6B5A23] border border-[#EBE5D8]",
  },
  awaiting_user_confirmation: {
    label: "Awaiting Confirmation",
    icon: Clock,
    pill: "bg-[#F5F2EB]/90 text-[#6B5A23] border border-[#EBE5D8]",
  },
  onramp_processing: {
    label: "In Progress",
    icon: Clock,
    pill: "bg-[#F5F2EB]/90 text-[#6B5A23] border border-[#EBE5D8]",
  },
  stablecoin_settlement_processing: {
    label: "In Progress",
    icon: Clock,
    pill: "bg-[#F5F2EB]/90 text-[#6B5A23] border border-[#EBE5D8]",
  },
  offramp_processing: {
    label: "In Progress",
    icon: Clock,
    pill: "bg-[#F5F2EB]/90 text-[#6B5A23] border border-[#EBE5D8]",
  },
  manual_review: {
    label: "Under Review",
    icon: AlertCircle,
    pill: "bg-[#F5F2EB]/90 text-[#6B5A23] border border-[#EBE5D8]",
  },
  failed: {
    label: "Failed",
    icon: XCircle,
    pill: "bg-[#FDE8E8] text-[#991B1B] border border-[#FAD2D2]",
  },
  cancelled: {
    label: "Cancelled",
    icon: XCircle,
    pill: "bg-[#FDE8E8] text-[#991B1B] border border-[#FAD2D2]",
  },
};

function CopyRow({ label, value, emphasize = false }) {
  const [copied, setCopied] = useState(false);

  if (
    value === undefined ||
    value === null ||
    String(value).trim() === ""
  ) {
    return null;
  }

  return (
    <div
      className={`flex items-center justify-between group ${
        emphasize
          ? "my-3 rounded-2xl bg-[#FFF7D6] px-4 py-3 border border-[#F3E7A4]"
          : "py-3 border-b border-white/10 last:border-0"
      }`}
    >
      <div className="min-w-0 pr-3">
        <p
          className={`text-[10px] uppercase tracking-wider font-semibold ${
            emphasize
              ? "text-[#0A4730]/50"
              : "text-[#F5F2EB]/50"
          }`}
        >
          {label}
        </p>

        <p
          className={`text-sm font-medium mt-0.5 break-all font-mono tracking-tight ${
            emphasize
              ? "text-[#0A2540] font-bold"
              : "text-[#F5F2EB]"
          }`}
        >
          {value}
        </p>

        {emphasize && (
          <p className="mt-1.5 text-[10px] leading-4 text-[#0A4730]/55 font-sans tracking-normal">
            Include this reference exactly so your payment can be matched to the transfer.
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={() => {
          navigator.clipboard.writeText(String(value));
          setCopied(true);

          setTimeout(() => {
            setCopied(false);
          }, 1500);
        }}
        className={`w-8 h-8 rounded-xl flex items-center justify-center active:scale-95 transition shrink-0 ml-3 shadow-inner ${
          emphasize
            ? "bg-white hover:bg-[#FFFDF4] text-[#0A4730]"
            : "bg-white/10 hover:bg-white/20 text-[#F5F2EB]/80"
        }`}
      >
        {copied ? (
          <Check
            size={14}
            className={
              emphasize
                ? "text-[#0A4730]"
                : "text-[#F5F2EB]"
            }
          />
        ) : (
          <Copy size={14} />
        )}
      </button>
    </div>
  );
}

function DepositInstructionsOverlay({
  instructions,
  onMinimize,
}) {
  const [showAdditionalDetails, setShowAdditionalDetails] =
    useState(false);

  if (!instructions) {
    return null;
  }

  const bankInstructions =
    instructions?.instructions &&
    typeof instructions.instructions === "object"
      ? instructions.instructions
      : instructions;

  const paymentRails = Array.isArray(
    bankInstructions?.payment_rails
  )
    ? bankInstructions.payment_rails.join(", ")
    : bankInstructions?.payment_rails;

  const accountName =
    bankInstructions?.account_name ||
    bankInstructions?.account_holder_name ||
    bankInstructions?.bank_beneficiary_name ||
    bankInstructions?.beneficiary_name || 
    "Bridge Building S.A.";

  const accountNumber =
    bankInstructions?.account_number ||
    bankInstructions?.bank_account_number;

  const routingNumber =
    bankInstructions?.routing_number ||
    bankInstructions?.bank_routing_number;

  const bicSwift =
    bankInstructions?.bic ||
    bankInstructions?.swift ||
    bankInstructions?.swift_code;

  const reference =
    instructions?.reference ||
    bankInstructions?.reference ||
    bankInstructions?.client_reference_id ||
    bankInstructions?.payment_reference ||
    bankInstructions?.transfer_reference;

  const amount =
    instructions?.amount ||
    bankInstructions?.amount;

  const currency =
    instructions?.currency ||
    bankInstructions?.currency;

  return (
    <div className="fixed inset-0 z-50 bg-[#01100b]/60 backdrop-blur-md flex items-end sm:items-center justify-center p-0 sm:p-4 animate-fadeIn">
      <div className="bg-[#01100b] border border-white/15 rounded-t-[2.5rem] sm:rounded-[2.5rem] max-w-lg w-full p-6 sm:p-9 shadow-2xl relative overflow-hidden max-h-[90vh] flex flex-col">

        {/* Subtle background glow effect */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#F5F2EB] opacity-[0.03] blur-[60px] rounded-full pointer-events-none" />

        <div className="flex items-center justify-between mb-5 shrink-0">
          <div className="w-12 h-12 rounded-2xl bg-[#F5F2EB]/10 border border-[#F5F2EB]/15 flex items-center justify-center shadow-inner">
            <Landmark
              size={22}
              className="text-[#F5F2EB]"
            />
          </div>

          <button
            type="button"
            onClick={onMinimize}
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-[#F5F2EB]/70 hover:text-white transition active:scale-95"
          >
            <X size={18} />
          </button>
        </div>

        <h2 className="text-xl sm:text-2xl font-serif font-semibold text-[#F5F2EB] mb-2 tracking-tight shrink-0">
          Complete your bank transfer
        </h2>

        <p className="text-sm text-[#F5F2EB]/70 leading-relaxed mb-6 shrink-0">
          Send{" "}
          <span className="font-bold text-[#F5F2EB] underline decoration-[#F5F2EB]/40 underline-offset-4">
            {currency} {amount}
          </span>{" "}
          to the account below. We'll detect it automatically and
          continue your transfer,{" "}
          <span className="text-[#F5F2EB] font-semibold">
            NO NEED TO REFRESH
          </span>
          .
        </p>

        <div className="overflow-y-auto pr-1 space-y-2 mb-6 custom-scrollbar">
          <div className="bg-black/20 backdrop-blur-md rounded-2xl px-5 py-2 border border-white/10 shadow-inner">
            <CopyRow
              label="Bank Name"
              value={bankInstructions?.bank_name}
            />

            <CopyRow
              label="Account Name"
              value={accountName}
            />

            <CopyRow
              label="IBAN"
              value={bankInstructions?.iban}
            />

            <CopyRow
              label="Account Number"
              value={accountNumber}
            />

            <CopyRow
              label="Sort Code"
              value={bankInstructions?.sort_code}
            />

            <CopyRow
              label="Routing Number"
              value={routingNumber}
            />

            <CopyRow
              label="BIC / SWIFT"
              value={bicSwift}
            />

            <CopyRow
              label="Reference (required)"
              value={reference}
              emphasize
            />
          </div>

          {/* Collapsible Section for Less Common / Optional Details */}
          <div className="pt-1">
            <button
              type="button"
              onClick={() =>
                setShowAdditionalDetails(
                  !showAdditionalDetails
                )
              }
              className="w-full flex items-center justify-between text-xs font-semibold text-[#F5F2EB]/60 hover:text-[#F5F2EB] px-2 py-2 transition-colors"
            >
              <span>
                Show additional bank details (Optional)
              </span>

              {showAdditionalDetails ? (
                <ChevronUp size={16} />
              ) : (
                <ChevronDown size={16} />
              )}
            </button>

            {showAdditionalDetails && (
              <div className="bg-black/20 backdrop-blur-md rounded-2xl px-5 py-2 border border-white/10 shadow-inner mt-2 animate-fadeIn">
                <CopyRow
                  label="Bank Code"
                  value={bankInstructions?.bank_code}
                />

                <CopyRow
                  label="Bank Address"
                  value={bankInstructions?.bank_address}
                />

                <CopyRow
                  label="Beneficiary Address"
                  value={
                    bankInstructions?.beneficiary_address ||
                    bankInstructions?.bank_beneficiary_address
                  }
                />

                <CopyRow
                  label="Payment Rail"
                  value={
                    bankInstructions?.payment_rail ||
                    paymentRails
                  }
                />
              </div>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={onMinimize}
          className="w-full h-14 rounded-2xl bg-[#F5F2EB] hover:text-white text-[#0A4730] text-sm font-bold tracking-wide active:scale-[0.98] transition shadow-lg hover:bg-[#EBE5D8] shrink-0"
        >
          I've sent it - close dialogue
        </button>
      </div>
    </div>
  );
}

const SUPPORT_EMAIL = "info@blu-pay.co";

function getSupportGmailUrl(transferId) {
  const subject = encodeURIComponent(
    `Help with transfer ${transferId}`
  );

  const body = encodeURIComponent(
    `Hi BluPay team,\n\nI need help with transfer ${transferId}.\n\n`
  );

  return `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(
    SUPPORT_EMAIL
  )}&su=${subject}&body=${body}`;
}

export function TransactionDetails() {
  const navigate = useNavigate();
  const { id: transferId } = useParams();

  const {
    transfer,
    isLoadingTransfer,
    transferError,
    transferDetail,
  } = useTransfer(transferId, {
    pollDetail: true,
  });

  const {
    fundingInstructions,
  } = useFundingInstructions(transferId, {
    poll: true,
  });

  const { recipient } = useRecipient(
    transfer?.recipient_id
  );

  const [minimized, setMinimized] = useState(false);

  // Automatically minimize if the onramp leg completes
  useEffect(() => {
    const onrampLeg = transferDetail?.legs?.find(
      (leg) => leg.leg_type === "onramp"
    );

    if (!onrampLeg) {
      return;
    }

    const onrampFinished = [
      "completed",
      "failed",
      "cancelled",
    ].includes(onrampLeg.status);

    if (onrampFinished) {
      setMinimized(true);
    }
  }, [transferDetail]);

  if (isLoadingTransfer) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center font-sans text-[#01100b]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-[#01100b] flex items-center justify-center text-[#F5F2EB] animate-pulse shadow-md">
            <ArrowUpRight
              size={18}
              strokeWidth={2.5}
            />
          </div>

          <p className="text-sm text-[#01100b]/60 font-medium tracking-wide">
            Loading transfer…
          </p>
        </div>
      </div>
    );
  }

  if (transferError || !transfer) {
    return (
      <div className="min-h-screen bg-transparent flex flex-col items-center justify-center p-6 text-center font-sans text-[#01100b]">
        <p className="text-sm text-[#01100b]/60 mb-4 font-medium">
          This transfer couldn't be found.
        </p>

        <button
          type="button"
          onClick={() =>
            navigate("/transactionhistory")
          }
          className="text-sm font-bold text-[#0A4730] underline underline-offset-4 hover:opacity-80 transition-opacity"
        >
          Back
        </button>
      </div>
    );
  }

  const primaryFundingInstructions = fundingInstructions ?? null;
  const fallbackFundingInstructions = transferDetail?.onramp_deposit_instructions ?? null;

  const depositInstructions =
    primaryFundingInstructions ||
    fallbackFundingInstructions ||
    null;

  const onrampLeg = transferDetail?.legs?.find(
    (leg) => leg.leg_type === "onramp"
  );

  // Determine if onramp is still expecting input / active
  const onrampFinished = onrampLeg 
    ? ["completed", "failed", "cancelled"].includes(onrampLeg.status)
    : false;

  // The overlay shows if we have instructions, it's not minimized, and the onramp isn't finished
  const showOverlay =
    Boolean(fallbackFundingInstructions) &&
    !minimized &&
    !onrampFinished;

  // The mini banner shows if instructions exist, it IS minimized, and the onramp isn't finished
  const showMiniBanner =
    Boolean(fallbackFundingInstructions) &&
    minimized &&
    !onrampFinished;

  const config =
    STATUS_DISPLAY[transfer.status] ??
    STATUS_DISPLAY.draft;

  const StatusIcon = config.icon;

  const date = new Date(
    transfer.created_at
  ).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  const time = new Date(
    transfer.created_at
  ).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });

  const recipientName =
    recipient?.nickname ||
    recipient?.full_name ||
    "—";

  const reference =
    transfer.public_reference ||
    transfer.id.slice(0, 8).toUpperCase();

  return (
    <div className="min-h-screen font-sans text-[#0A4730] overflow-x-hidden selection:bg-[#F5F2EB] selection:text-[#0A4730]">

      {/* Generic bank-transfer instructions overlay */}
      {showOverlay && (
        <DepositInstructionsOverlay
          instructions={depositInstructions}
          onMinimize={() =>
            setMinimized(true)
          }
        />
      )}

      {/* Mini banner to reopen overlay if instructions exist and minimized */}
      {showMiniBanner && (
        <button
          type="button"
          onClick={() =>
            setMinimized(false)
          }
          className="fixed left-[30%] bottom-[10%] md:bottom-6 md:left-1/2 -translate-x-1/2 z-40 bg-[#0A4730] text-[#F5F2EB] text-xs font-semibold px-5 py-3 rounded-full shadow-2xl flex items-center gap-2.5 active:scale-95 transition border border-white/20 hover:border-white/40 backdrop-blur-md"
        >
          <Landmark
            size={15}
            className="text-[#F5F2EB]"
          />

          <span>
            Bank transfer still needed
          </span>
        </button>
      )}

      {/* Ambient background decorative glow */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute w-[600px] h-[600px] bg-[#EBE5D8] opacity-30 blur-[140px] rounded-full -top-32 -left-32" />
        <div className="absolute w-[400px] h-[400px] bg-[#EBE5D8] opacity-20 blur-[120px] rounded-full bottom-0 right-0" />
      </div>

      {/* Nav */}
      <div className="relative z-10 w-full max-w-xl mx-auto px-6 py-6 flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm font-medium text-[#0A4730]/70 hover:text-[#0A4730] transition-colors active:opacity-60 bg-white/50 backdrop-blur-sm px-3.5 py-2 rounded-xl border border-[#0A4730]/5 shadow-sm"
        >
          <ArrowLeft size={16} />
          <span>Back</span>
        </button>
      </div>

      <div className="relative z-10 w-full max-w-xl mx-auto px-6 pb-20 space-y-5">
        <PrivyCardOnrampButton transferId={transferId} />

        {/* Hero Amount Card */}
        <div className="bg-[#0A4730] rounded-[2.5rem] p-8 sm:p-10 text-center relative overflow-hidden shadow-2xl border border-white/10">
          <div className="absolute inset-0 pointer-events-none opacity-[0.07]"
            style={{
              backgroundSize: "24px 24px",
            }}
          />
          <div className="absolute top-[-80px] left-1/2 -translate-x-1/2 w-[350px] h-[350px] bg-[#F5F2EB] opacity-[0.07] blur-[90px] rounded-full pointer-events-none" />

          <div className="relative z-10 flex flex-col items-center gap-5">
            <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[11px] font-bold tracking-widest uppercase shadow-sm ${config.pill}`}>
              <StatusIcon size={13} strokeWidth={2.5} />
              <span>{config.label}</span>
            </div>

            <div>
              <p className="text-[11px] font-bold tracking-widest uppercase text-[#F5F2EB]/60 mb-1.5">
                You'll send
              </p>
              <p className="text-4xl sm:text-5xl font-serif font-semibold text-[#F5F2EB] tracking-tight tabular-nums">
                {transfer.send_currency} {Number(transfer.send_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>

            <div className="flex items-center gap-3 w-full max-w-[200px] my-1">
              <div className="flex-1 h-px bg-white/15" />
              <div className="w-8 h-8 rounded-full bg-white/10 border border-white/10 flex items-center justify-center shadow-inner">
                <ArrowUpRight size={15} className="text-[#F5F2EB] rotate-45" />
              </div>
              <div className="flex-1 h-px bg-white/15" />
            </div>

            <div>
              <p className="text-[11px] font-bold tracking-widest uppercase text-[#F5F2EB]/60 mb-1.5">
                {recipientName} receives
              </p>
              <p className="text-2xl sm:text-3xl font-serif font-semibold text-[#F5F2EB] tracking-tight tabular-nums">
                {transfer.receive_currency} {Number(transfer.receive_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>

            <div className="pt-2 border-t border-white/10 w-full max-w-[240px]">
              <p className="text-[11px] text-[#F5F2EB]/50 font-medium tracking-wide">{date} · {time}</p>
            </div>
          </div>
        </div>

        {/* Details Card */}
        <div className="bg-white/80 backdrop-blur-md rounded-[2.5rem] border border-[#0A4730]/10 shadow-xl overflow-hidden p-2">
          <div className="px-6 pt-6 pb-2">
            <p className="text-[10px] font-bold tracking-widest uppercase text-[#0A4730]/50">
              Transfer details
            </p>
          </div>

          <div className="px-6 pb-6 space-y-0 divide-y divide-[#0A4730]/5">
            <DetailRow label="Recipient" value={recipientName} />
            <DetailRow
              label="Exchange rate"
              value={`1 ${transfer.send_currency} = ${Number(transfer.exchange_rate).toFixed(4)} ${transfer.receive_currency}`}
            />
            {transfer.failure_reason && (
              <div className="py-4">
                <p className="text-[11px] font-bold text-red-500 uppercase tracking-wider mb-1.5">
                  Reason for failure
                </p>
                <p className="text-sm text-red-700 leading-relaxed font-medium">{transfer.failure_reason}</p>
              </div>
            )}

            <div className="py-4 flex justify-between items-center gap-4">
              <span className="text-[12px] font-medium text-[#0A4730]/50 shrink-0">Reference</span>
              <button
                type="button"
                onClick={() => navigator.clipboard.writeText(reference)}
                className="flex items-center gap-2.5 active:opacity-50 transition-opacity group bg-[#0A4730]/5 hover:bg-[#0A4730]/10 px-3 py-1.5 rounded-xl border border-[#0A4730]/5"
              >
                <span className="text-[12px] font-bold text-[#0A4730] font-mono tracking-wider">
                  {reference}
                </span>
                <div className="w-5 h-5 rounded-lg bg-[#0A4730]/10 flex items-center justify-center transition-colors">
                  <Copy size={11} className="text-[#0A4730]/70" />
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Support Card */}
        <button
          type="button"
          onClick={() => {
            window.open(
              getSupportGmailUrl(transferId),
              "_blank",
              "noopener,noreferrer"
            );
          }}
          className="w-full bg-white/80 backdrop-blur-md rounded-[2.2rem] border border-[#0A4730]/10 shadow-xl px-6 py-5 flex items-center justify-between active:bg-white hover:bg-white transition-all group"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#0A4730]/5 border border-[#0A4730]/10 flex items-center justify-center text-[#0A4730] shadow-inner group-hover:scale-105 transition-transform">
              <MessageCircle size={20} />
            </div>
            <div className="text-left">
              <p className="text-[13px] font-bold text-[#0A4730] tracking-tight">Need help with this transfer?</p>
              <p className="text-[11px] text-[#0A4730]/60 mt-0.5 font-medium">Chat with our support team</p>
            </div>
          </div>
          <div className="w-9 h-9 rounded-xl bg-[#0A4730]/5 flex items-center justify-center text-[#0A4730]/40 group-hover:text-[#0A4730] group-hover:bg-[#0A4730]/10 transition-all">
            <ArrowUpRight size={16} />
          </div>
        </button>

        {/* General support page */}
        <button
          type="button"
          onClick={() => navigate("/support")}
          className="mt-3 w-full py-3 text-sm font-semibold text-[#0A4730]/70 hover:text-[#0A4730] transition-colors"
        >
          Or visit Blupay's Support Page
        </button>
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="py-4 flex justify-between items-baseline gap-6">
      <span className="text-[12px] font-medium text-[#0A4730]/50 shrink-0">{label}</span>
      <span className="text-[13px] font-bold text-[#0A4730] text-right leading-snug tracking-tight">{value}</span>
    </div>
  );
}
